import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { postForm } from '@/shared/api/client';
import { formatCost, formatUnixDateTime } from '@/shared/lib/datetime';
import { formatNumber } from '@/shared/lib/formatNumber';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { FilterDateTime } from '@/shared/ui/FilterDateTime';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import type { SelectOption } from '@/shared/ui/selectTypes';
import { fetchPartnerOptions, fetchPartnerTypeOptions } from './api';

interface Transaction extends Record<string, unknown> {
  transaction_id: number;
  customer_id: number;
  card?: string;
  transaction_time?: number;
  amount?: number;
  cashback_sum?: number;
  user_registration_time?: number;
  cashback?: number;
}

interface PartnersResponse extends Record<string, unknown> {
  items?: Transaction[];
  sum?: number | string;
  sum_cashback?: number | string;
  pagination?: { pageSize?: number; totalCount?: number; pagesCount?: number };
}

const FULL = { width: '100%' as const };

const dateToUnix = (value: Date | null): number | '' =>
  value ? Math.floor(value.getTime() / 1000) : '';

const SumItem = ({ title, value }: { title: string; value: string }) => (
  <div className="partners-sum-item">
    <div className="partners-sum-title">{title}</div>
    <div className="partners-sum-value">{value}</div>
  </div>
);

export const PartnersPage = () => {
  const [page, setPage] = useState(1);
  const [draftType, setDraftType] = useState('');
  const [draftPartner, setDraftPartner] = useState('');
  const [draftCustomerId, setDraftCustomerId] = useState('');
  const [draftFrom, setDraftFrom] = useState<Date | null>(null);
  const [draftTo, setDraftTo] = useState<Date | null>(null);

  const [applied, setApplied] = useState({
    partnerId: '',
    customerId: '',
    from: null as Date | null,
    to: null as Date | null,
  });
  const [searched, setSearched] = useState(false);

  const typesQuery = useQuery({
    queryKey: ['partner-types'],
    queryFn: fetchPartnerTypeOptions,
  });

  const partnersQuery = useQuery({
    queryKey: ['partner-options', draftType],
    queryFn: () => fetchPartnerOptions(draftType),
  });

  const listParams = useMemo(
    () => ({
      page,
      partner_id: applied.partnerId,
      customer_id: applied.customerId,
      create_time_from: dateToUnix(applied.from),
      create_time_to: dateToUnix(applied.to),
    }),
    [applied, page],
  );

  const listQuery = useQuery({
    queryKey: ['marketing-partners', listParams],
    queryFn: () =>
      postForm<PartnersResponse>('/billing/billing-payment/get-partner-payments/', listParams),
    enabled: searched,
  });

  const rows = getListRows<Transaction>(listQuery.data, ['items']);
  const totalPages = getTotalPages(listQuery.data);
  const pageSize = Number(listQuery.data?.pagination?.pageSize) || 20;
  const totalCount = Number(listQuery.data?.pagination?.totalCount) || 0;
  const sum = listQuery.data?.sum;
  const sumCashback = listQuery.data?.sum_cashback;

  const canSearch = Boolean(
    draftCustomerId.trim() || draftPartner || draftType || draftFrom || draftTo,
  );

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    if (!canSearch) return;
    setPage(1);
    setApplied({
      partnerId: draftPartner,
      customerId: draftCustomerId.trim(),
      from: draftFrom,
      to: draftTo,
    });
    setSearched(true);
  };

  const onTypeChange = (value: string) => {
    setDraftType(value);
    setDraftPartner('');
  };

  const partnerOptions: SelectOption[] = partnersQuery.data ?? [];
  const typeOptions: SelectOption[] = typesQuery.data ?? [];

  return (
    <>
      <section className="content-header">
        <h1>Партнёры</h1>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <form onSubmit={onSearch} className="partners-filters">
              <div className="partners-filters-row">
                <div className="partners-filters-col">
                  <span className="partners-filter-label">Тип партнера</span>
                  <FilterSelect
                    {...FULL}
                    options={typeOptions}
                    value={draftType}
                    placeholder="Выбрать тип..."
                    isLoading={typesQuery.isLoading}
                    isClearable={false}
                    onChange={onTypeChange}
                  />
                </div>
                <div className="partners-filters-col">
                  <span className="partners-filter-label">Партнёры</span>
                  <FilterSelect
                    {...FULL}
                    options={partnerOptions}
                    value={draftPartner}
                    placeholder="Выбрать партнера..."
                    isLoading={partnersQuery.isFetching}
                    isClearable={false}
                    onChange={setDraftPartner}
                  />
                </div>
              </div>

              <div className="partners-filters-row partners-filters-row-search">
                <div className="partners-filters-col">
                  <input
                    className="form-control partners-filter-input"
                    placeholder="Поиск по id автовладельца..."
                    value={draftCustomerId}
                    onChange={(e) => setDraftCustomerId(e.target.value)}
                  />
                </div>
                <div className="partners-filters-col partners-filters-dates">
                  <FilterDateTime
                    value={draftFrom}
                    onChange={setDraftFrom}
                    placeholder="Начиная с даты..."
                  />
                  <FilterDateTime
                    value={draftTo}
                    onChange={setDraftTo}
                    placeholder="Заканчивая датой..."
                  />
                  <button type="submit" className="btn btn-primary" disabled={!canSearch || listQuery.isFetching}>
                    Найти
                  </button>
                </div>
              </div>

              <div className="partners-sums">
                <SumItem
                  title="Общая сумма транзакций"
                  value={`${formatCost(searched ? sum : 0)} ₽`}
                />
                <SumItem
                  title="Общее число транзакций"
                  value={formatNumber(searched ? totalCount : 0)}
                />
                <SumItem
                  title="Общая сумма кэшбека"
                  value={`${formatCost(searched ? sumCashback : 0)} ₽`}
                />
              </div>
            </form>
          </div>

          <div className="box-body">
            <QueryStatus
              isLoading={listQuery.isLoading}
              error={listQuery.error}
              errorText="Ошибка загрузки транзакций партнёров"
            />
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>№</th>
                    <th>№ карты</th>
                    <th>ID транзакции</th>
                    <th>Дата и время транзакции</th>
                    <th>Сумма транзакции</th>
                    <th>Сумма кэшбека</th>
                    <th>ID автовладельца</th>
                    <th>Дата регистрации автовладельца</th>
                    <th>Размер кэшбэка</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((tx, index) => (
                    <tr key={tx.transaction_id}>
                      <td>{(page - 1) * pageSize + index + 1}</td>
                      <td>{tx.card}</td>
                      <td>{tx.transaction_id}</td>
                      <td>{formatUnixDateTime(tx.transaction_time)}</td>
                      <td>{formatCost(tx.amount)} ₽</td>
                      <td>{formatCost(tx.cashback_sum)} ₽</td>
                      <td>
                        <Link to={`/users/user/${tx.customer_id}`}>{tx.customer_id}</Link>
                      </td>
                      <td>{formatUnixDateTime(tx.user_registration_time)}</td>
                      <td>{tx.cashback} %</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={searched ? totalPages : 1} onChange={setPage} />
          </div>
        </div>
      </section>
    </>
  );
};
